import { createFileRoute, Link } from '@tanstack/react-router';
import { Button } from '@bookaburra/ui';
import { Section, SectionEyebrow } from '../components/Section.js';
import { BookingMockup } from '../components/visuals/BookingMockup.js';
import { SickDayMockup } from '../components/visuals/SickDayMockup.js';
import {
  IconImaging,
  IconClinic,
  IconTrades,
  IconPersonal,
} from '../components/visuals/IndustryIcons.js';
import { usePageTitle } from '../components/usePageTitle.js';
import { signupUrl } from '../lib/urls.js';

export const Route = createFileRoute('/')({
  component: HomePage,
});

function HomePage() {
  usePageTitle('Bookings that only offer what your team can actually do');

  return (
    <>
      {/* HERO */}
      <section className="bg-brand-cream">
        <div className="mx-auto max-w-6xl px-6 pb-20 pt-16 sm:px-12 sm:pt-24 lg:grid lg:grid-cols-[5fr_6fr] lg:gap-16 lg:pt-28">
          <div>
            <h1 className="text-4xl font-extrabold leading-[1.05] tracking-tightest text-brand-ink-soft sm:text-6xl lg:text-[4.5rem]">
              Give your clients a booking page{' '}
              <span className="text-brand-sage">your team can actually trust.</span>
            </h1>
            <p className="mt-7 max-w-xl text-lg text-brand-ink-soft/80 sm:text-xl">
              We believe scheduling should protect your team&rsquo;s focus and respect your clients&rsquo; time.
              Bookaburra coordinates rooms, skills, and travel dynamically—so you only offer what you can actually deliver.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Button asChild size="lg">
                <a href={signupUrl()}>Start for free &middot; no card needed</a>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/pricing">See pricing</Link>
              </Button>
            </div>
            <p className="mt-6 text-sm text-brand-ink-soft/55">
              Free to start &middot; upgrade any time
            </p>
          </div>

          <div className="mt-16 lg:mt-0">
            <BookingMockup />
          </div>
        </div>
      </section>

      {/* TRUST BAND — industry markers */}
      <section className="border-y border-brand-sage/10 bg-brand-cream-shadow">
        <div className="mx-auto max-w-6xl px-6 py-8 sm:px-12">
          <p className="text-center text-xs font-semibold uppercase tracking-widest text-brand-ink-soft/55">
            Built for service teams across
          </p>
          <ul className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-3">
            {industryMarkers.map((m) => (
              <li
                key={m.label}
                className="flex items-center justify-center gap-3 text-brand-sage"
              >
                <m.icon className="h-7 w-7" />
                <span className="text-sm font-medium text-brand-ink-soft">
                  {m.label}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* PROBLEMS WE SOLVE */}
      <Section>
        <div className="max-w-3xl">
          <SectionEyebrow>Calmer days, outstanding service</SectionEyebrow>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-brand-ink-soft sm:text-5xl">
            Keep your focus where it belongs.
          </h2>
          <p className="mt-5 text-lg text-brand-ink-soft/75">
            When your logistics are handled, your team can show up at their best.
            Bookaburra dynamically coordinates staff skills, room availability, and travel buffers in the background,
            ensuring every booking goes to the right place and the right person.
          </p>
        </div>

        <div className="mt-14 grid gap-5 sm:grid-cols-2">
          {problems.map((p) => (
            <article
              key={p.linkLabel}
              className="group relative flex flex-col rounded-2xl border border-brand-sage/15 bg-brand-cream-shadow p-7 transition-colors hover:border-brand-sage/40 hover:bg-brand-cream"
            >
              <div className="mb-5 flex items-center gap-3 text-brand-sage">
                <p.icon className="h-6 w-6" />
                <span className="text-xs font-semibold uppercase tracking-widest">
                  {p.linkLabel}
                </span>
              </div>
              <blockquote className="text-xl font-semibold leading-snug text-brand-ink-soft">
                &ldquo;{p.quote}&rdquo;
              </blockquote>
              <p className="mt-4 text-base text-brand-ink-soft/75">{p.answer}</p>
              <Link
                to={p.linkTo}
                className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-brand-sage underline-offset-4 hover:underline"
              >
                Read the case study
                <span aria-hidden>&rarr;</span>
              </Link>
            </article>
          ))}
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <Section tone="shadow">
        <div className="lg:grid lg:grid-cols-[5fr_6fr] lg:gap-16 lg:items-center">
          <div>
            <SectionEyebrow>Logistics that respect your team</SectionEyebrow>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-brand-ink-soft sm:text-5xl">
              Logistics designed to support your work.
            </h2>
            <ol className="mt-10 space-y-8">
              {steps.map((step, i) => (
                <li key={step.title} className="flex gap-5">
                  <span
                    aria-hidden
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-sage text-sm font-bold text-brand-cream"
                  >
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="text-lg font-semibold text-brand-ink-soft">
                      {step.title}
                    </h3>
                    <p className="mt-2 text-base text-brand-ink-soft/75">
                      {step.body}
                    </p>
                    {i === 2 ? (
                      <a href="https://bookaburra.com/features#sick-day-reshuffle" target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-brand-sage underline-offset-4 hover:underline">
                        See how it works <span aria-hidden>→</span>
                      </a>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="mt-14 lg:mt-0">
            <SickDayMockup />
            <p className="mt-4 text-center text-xs text-brand-ink-soft/55">
              Review a sick-day reshuffle before clients hear about it.
            </p>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <section className="bg-brand-ink text-brand-cream">
        <div className="mx-auto max-w-6xl px-6 py-20 sm:px-12 sm:py-28">
          <div className="grid gap-10 lg:grid-cols-[3fr_2fr] lg:items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-brand-sage-light">
                Experience the difference
              </p>
              <h2 className="mt-3 text-4xl font-extrabold tracking-tight sm:text-6xl">
                Restore calm to your calendar.
              </h2>
              <p className="mt-6 max-w-xl text-lg text-brand-cream/80">
                We believe booking software should support your craft, not create administrative headaches.
                Start for free today and experience a calmer, more productive team week.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <a
                href={signupUrl()}
                className="inline-flex items-center justify-center rounded-md bg-brand-cream px-6 py-3.5 text-sm font-medium text-brand-ink-soft hover:bg-brand-cream/90"
              >
                Start for free
              </a>
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center rounded-md border border-brand-cream/30 px-6 py-3.5 text-sm font-medium text-brand-cream hover:bg-brand-cream/10"
              >
                See pricing
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

const industryMarkers = [
  { icon: IconClinic, label: 'Allied health' },
  { icon: IconTrades, label: 'Mobile trades' },
  { icon: IconPersonal, label: 'Personal training' },
] as const;

const problems = [
  {
    icon: IconImaging,
    quote: 'Your CT room gets booked for an X-ray and the CT patient waits.',
    answer:
      'Bookaburra keeps the flexible room free for the work that needs it.',
    linkTo: '/for-medical-imaging',
    linkLabel: 'Medical imaging',
  },
  {
    icon: IconClinic,
    quote:
      'The physio is in Consult 1 when the treatment room was free — and a GP is stuck.',
    answer:
      'Each service is matched to the room it actually needs.',
    linkTo: '/for-allied-health',
    linkLabel: 'Clinics & allied health',
  },
  {
    icon: IconTrades,
    quote: "You can't be at two addresses fifteen minutes apart.",
    answer:
      'Travel time is built into every on-site slot.',
    linkTo: '/for-trades',
    linkLabel: 'Mobile trades',
  },
  {
    icon: IconPersonal,
    quote:
      'A 6am bootcamp gets a fourth booking when only three spots were left.',
    answer:
      'Group sessions enforce capacity. The next client sees the next real slot.',
    linkTo: '/for-personal-training',
    linkLabel: 'Personal training',
  },
] as const;

const steps = [
  {
    title: 'Define your capabilities',
    body: "Map out your team's skills, rooms, and equipment. Bookaburra treats these as hard guidelines, not suggestions.",
  },
  {
    title: 'Let clients book with confidence',
    body: 'Whether clients book via web, phone, or messaging, they are only presented with slots your team is fully equipped to deliver.',
  },
  {
    title: 'Handle exceptions with grace',
    body: 'When a staff member calls in sick, Bookaburra plans a reshuffle. Review and adjust the layout before any notifications are dispatched.',
  },
];
